import { forwardRef, memo, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Avatar from '@mui/material/Avatar';
// Skillmore UI Components
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
// local imports
import { SuccessStory } from '../graphql/types';
// SCSS imports
import { card, cardInactive, avatar, storyText, storyRole } from './SuccessStoryCard.module.scss';

const containerSx: SxProps<Theme> = {
  px: { sm: 1.75 },
  pt: { xs: 8, sm: 11 },
  pb: { xs: 3, sm: 7 }
};

type SuccessStoryCardProps = {
  readonly story: SuccessStory;
  readonly isCurrent?: boolean;
};

const SuccessStoryCard = forwardRef<HTMLDivElement, SuccessStoryCardProps>(({
  story: { photo, name, title, story, link },
  isCurrent = false,
}, ref) => {
  const { formatMessage } = useIntl();

  const values = useMemo(() => ({
    text: formatMessage({ id: story, defaultMessage: story }),
    link: link ? (
      <StandardLink
          href={isCurrent ? formatMessage({ id: link, defaultMessage: link }) : undefined}
          text="board.stories.more"
          variant="inherit"
      />
    ) : ''
  }), [story, link, isCurrent, formatMessage]);

  return (
    <Box sx={containerSx}>
      <Card
          ref={ref}
          elevation={isCurrent ? 9 : 0}
          className={isCurrent ? card : cardInactive}
      >
        {photo ? <Avatar alt={name} src={photo} className={avatar}/> : undefined}
        <Box className={storyText}>
          <FormattedMessage id="board.stories.text" values={values}/>
        </Box>
        <Box textAlign="center">
          <BoxTypography variant="h3" pb={0.25}>
            <FormattedMessage id={name} defaultMessage={name}/>
          </BoxTypography>
          <Box color="text.label" className={storyRole}>
            <FormattedMessage id={title} defaultMessage={title}/>
          </Box>
        </Box>
      </Card>
    </Box>
  );
});

SuccessStoryCard.displayName = 'SuccessStoryCard';

export default memo(SuccessStoryCard);
